import React from "react";
import { Users, UserPlus, UserCheck, UserX, Building2, ShieldOff, Plane, Layers } from "lucide-react";
import { api } from "../../lib/api";
import PageHeader from "../../components/PageHeader";
import { StatCard, StatSkeleton, EmptyState, PanelCard, PanelSkeleton } from "./_shared";

/** AP-021 · HR dashboard variant (RH role). */
export default function DashboardHR() {
  const [data, setData] = React.useState(null);
  const [cards, setCards] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    let cancelled = false;
    Promise.allSettled([
      api.get("/stats/hr"),
      api.get("/admin/hr/dashboard-cards"),
    ]).then((rs) => {
      if (cancelled) return;
      if (rs[0].status === "fulfilled") setData(rs[0].value.data);
      if (rs[1].status === "fulfilled") setCards(rs[1].value.data);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div data-testid="dashboard-hr" className="space-y-6">
      <PageHeader
        testid="dashboard-hr-header"
        title="Painel · RH"
        subtitle="Recursos Humanos — headcount, admissões, portal e departamentos."
      />

      {/* AP-022.1 · 6 stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {loading ? (
          <>
            {[...Array(6)].map((_, i) => <StatSkeleton key={i} />)}
          </>
        ) : (
          <>
            <StatCard testid="hr-active"    label="Ativos"          value={cards?.active ?? 0}         icon={UserCheck} accent />
            <StatCard testid="hr-inactive"  label="Inativos"         value={cards?.inactive ?? 0}       icon={UserX} />
            <StatCard testid="hr-new"       label="Novos este mês"   value={cards?.new_this_month ?? 0} icon={UserPlus} accent />
            <StatCard testid="hr-vacation"  label="Em férias"        value={cards?.on_vacation ?? 0}    icon={Plane} />
            <StatCard testid="hr-dismissed" label="Desligados"       value={cards?.dismissed ?? 0}      icon={ShieldOff} />
            <StatCard testid="hr-total"     label="Total"            value={cards?.total ?? 0}          icon={Layers} />
          </>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PanelCard title="Por departamento" testid="hr-by-department" icon={Building2}>
          {loading ? <PanelSkeleton /> : (
            (data?.by_department || []).length === 0
              ? <EmptyState label="Nenhum departamento cadastrado ainda." />
              : (
                <ul className="divide-y divide-border">
                  {data.by_department.map((d) => (
                    <li key={d.department} className="flex items-center justify-between py-2 text-sm">
                      <span className="truncate">{d.department}</span>
                      <span className="font-mono text-xs text-muted-foreground">{d.count}</span>
                    </li>
                  ))}
                </ul>
              )
          )}
        </PanelCard>

        <PanelCard title="Últimas admissões" testid="hr-recent-admissions" icon={Users}>
          {loading ? <PanelSkeleton /> : (
            (data?.recent_admissions || []).length === 0
              ? <EmptyState label="Ainda não há admissões registradas." />
              : (
                <ul className="divide-y divide-border">
                  {data.recent_admissions.map((e) => (
                    <li key={e.id} className="py-2 text-sm flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium truncate">{e.full_name}</p>
                        <p className="text-[11px] text-muted-foreground truncate">{e.position || "—"} · {e.department || "—"}</p>
                      </div>
                      <span className="text-[11px] text-muted-foreground shrink-0">{e.admission_date || "—"}</span>
                    </li>
                  ))}
                </ul>
              )
          )}
        </PanelCard>
      </div>
    </div>
  );
}
